import { BookOpen } from 'lucide-react-native';
import { memo, useCallback } from 'react';
import { twJoin } from 'tailwind-merge';

import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

type ReferencesActionProps = {
  onPress: () => void;
  isSending: boolean;
};

/**
 * Toolbar button that opens the URL-entry action sheet so the user can
 * proactively add formatted references to the conversation.
 *
 * Available to all participants. The action sheet, batch-format endpoint, and
 * per-reference message posting are the same ones the prototype bubble flow
 * uses — this button just provides a bubble-independent entry point.
 */
export const ReferencesAction = memo(function ReferencesAction({
  onPress,
  isSending,
}: ReferencesActionProps) {
  const handlePress = useCallback(() => {
    if (isSending) {
      return;
    }

    onPress();
  }, [isSending, onPress]);

  return (
    <Pressable
      disabled={isSending}
      onPress={handlePress}
      hitSlop={ICON_BUTTON_HIT_SLOP}
      className="h-10 w-10 items-center justify-center rounded-full active:opacity-70"
      {...getAccessibilityProps({
        label: 'Add references',
        role: 'button',
        hint: 'Opens a form to paste reference URLs',
      })}>
      <Icon
        as={BookOpen}
        size="lg"
        className={twJoin(isSending && 'text-typography-300', !isSending && 'text-primary-500')}
      />
    </Pressable>
  );
});
